import { Subscription } from 'rxjs';
import { AppConstants } from '@unpispas/upp-defs';

import { dataService } from '../../data';

import { DataObject, ObjectOptions, RelatedObject } from "../base";
import { CreateObject } from "../item";
import { TicketView, TicketViewProxy } from '../views/ticket';

import { Client } from './client';
import { Place } from './place';
import { QrCode } from './qrcode';
import { TicketData } from './ticketdata';
import { PayAccount } from './payaccount';
import { TicketInvoice } from './ticketinvoice';
import { AccountInvoice } from './accountinvoice';

import type { Session } from './session';

export type TicketPayment = 'PAYCASH' | 'PAYCARD' | 'PAYLINE' | 'PAYMIXED' | 'PAYACCOUNT';
export enum TicketSource {
    BR = 'BR',   // Bar / waiter default origin
    SF = 'SF',   // Staff ticket (waiter session)
    QR = 'QR',   // Customer QR/table ticket
    HM = 'HM'    // Home / web ticket
}

/****************************/
/* SCHEMA DEFINITION        */
/****************************/

import { SessionType } from '../types';
import { PlaceType } from '../types';
import { QrCodeType } from '../types';
import { TicketChangeType } from '../types';
import { TicketDataType } from '../types';
import { TicketInvoiceType } from '../types';
import { TicketProductType } from '../types';
import { TicketOfferType } from '../types';
import { TicketExtraType } from '../types'; 
import { TicketDiscountType } from '../types';
import { MixedPaymentType } from '../types';
import { PinpadPayType } from '../types';
import { PayAccountType } from '../types';
import { PaymentType } from '../types';
import { AccountInvoiceType } from '../types';
import { TillType } from '../types';
import { AuditType } from '../types';
import { AskWaiterType } from '../types';

const _schema = {
    name: "TICKET",
    "fields": [
        { name: "created", type: "date", volatile: true },
        { name: "updated", type: "date", volatile: true },
        { name: "status", type: "string", default: "PR" },
        { name: "session", type: "objid" },
        { name: "paidby", type: "objid" },
        { name: "paidon", type: "date" },
        { name: "paidin", type: "objid" },
        { name: "till", type: "objid" },
        { name: "audit", type: "objid" },
        { name: "place", type: "objid" },
        { name: "qrcode", type: "objid" },
        { name: "price", type: "number" },
        { name: "refund", type: "number", default: "0" }, 
        { name: "taxes", type: "number" },
        { name: "diners", type: "number" },
        { name: "orderno", type: "string" },
        { name: "series", type: "string", default: "S00000000" },
        { name: "invceno", type: "string" },
        { name: "elapsed", type: "number" },
        { name: "comments", type: "string" },
        { name: "payment", type: "string" },
        { name: "transaction", type: "objid" },
        { name: "account", type: "objid" },
        { name: "invceac", type: "objid" },
        { name: "given", type: "number" },
        { name: "flags", type: "string" },
        { name: "tmcommit", type: "date" }
    ],
    relate: [
        { direction: ">", target: "SESSION", by: "session", name: "session", reason: "session", child: false, class: SessionType },
        { direction: ">", target: "PLACE", by: "place", name: "place", reason: "place", child: false, class: PlaceType },
        { direction: ">", target: "QRCODE", by: "qrcode", name: "qrcode", reason: "qrcode", child: false, class: QrCodeType },
        { direction: ">", target: "SESSION", by: "paidby", name: "paidby", reason: "paidby", child: false, class: SessionType },
        { direction: ">", target: "SESSION", by: "paidin", name: "paidin", reason: "paidin", child: false, class: SessionType },
        { direction: ">", target: "TILL", by: "till", name: "till", reason: "till", child: false, class: TillType },
        { direction: ">", target: "AUDIT", by: "audit", name: "audit", reason: "audit", child: false, class: AuditType },
        { direction: ">", target: "PAYACCOUNT", by: "account", name: "account", reason: "account", child: true, class: PayAccountType },
        { direction: ">", target: "PAYMENT", by: "transaction", name: "transaction", reason: "transaction", child: true, class: PaymentType },
        { direction: ">", target: "ACCOUNTINVOICE", by: "invceac", name: "invceac", reason: "invceac", child: true, class: AccountInvoiceType },
        { direction: "<", target: "TICKETPRODUCT", by: "ticket", name: "product", reason: "products", child: true, class: TicketProductType },
        { direction: "<", target: "TICKETOFFER", by: "ticket", name: "offer", reason: "offers", child: true, class: TicketOfferType },
        { direction: "<", target: "TICKETEXTRA", by: "ticket", name: "extra", reason: "extras", child: true, class: TicketExtraType },
        { direction: "<", target: "TICKETDISCOUNT", by: "ticket", name: "discount", reason: "discounts", child: true, class: TicketDiscountType },
        { direction: "<", target: "TICKETMIXEDPAY", by: "ticket", name: "mixed", reason: "mixed", child: true, class: MixedPaymentType },
        { direction: "<", target: "PINPADPAY", by: "ticket", name: "pinpadpay", reason: "pinpadpays", child: true, class: PinpadPayType },
        { direction: "<", target: "ASKWAITER", by: "ticket", name: "request", reason: "requests", child: true, class: AskWaiterType },
        { direction: "<", target: "TICKETCHANGE", by: "ticket", name: "change", reason: "changes", child: true, class: TicketChangeType },
        { direction: "<", target: "TICKETDATA", by: "ticket", name: "data", reason: "datas", child: true, class: TicketDataType },
        { direction: "<", target: "TICKETINVOICE", by: "ticket", name: "invoice", reason: "invoices", child: true, class: TicketInvoiceType }
    ]
} as const;

const _TicketClass = CreateObject(_schema);
if (!_TicketClass) {
    throw new Error("Failed to create BaseClass for '" + _schema.name + "'");
}

/****************************/
/* CLASS DECLARATION        */
/****************************/

enum TicketFlags {
    OrderPrinted = 0,       // the 'new ticket' has been sent to printer
    ReadyNotified = 1,      // the 'ticket ready' has been notified to client
    TicketReady = 2,        // the ticket is ready
    TicketToPay = 3,        // ticket payment has been requested
    TicketPaid = 4,         // ticket has been paid
    TicketReopened = 5,     // ticket was reopened after payment
    TicketToCommit = 6          // sticky: ticket must be committed (cannot be discarded)
};

export class Ticket extends _TicketClass {
    private _oneminute_subscription: Subscription | null = null;
    private _isrecent = false;
    private _iserror = false;

    constructor(objid: string | null, data: dataService, objoptions: ObjectOptions | null = null){
        super(_schema, objid, data, objoptions || {});

        if (!this._data['flags']){  // initialize the flags value (cannot be null)
            this._data['flags'] = '0'.repeat(Object.keys(TicketFlags).length / 2);
        }

        // Setter + objid ⇒ ToUpdate; the changelog OnChange is then ignored.
        if (!this.objid && !this.created){
            this.created = new Date();
        }

        this._IsRecentCheck();
    }

    Copy(store: Array<DataObject> = []): Ticket {
        return this._Copy(store) as Ticket;
    }

    override get viewProxy(): TicketView {
        return TicketViewProxy(this, this.data);
    }

    protected override OnUpdate(info: any): boolean {
        const changed = super.OnUpdate(info);
        if (changed) {
            this._IsRecentCheck();
        }

        return changed;
    }

    override DoRefresh(table: string | string[], upwards = true): void {
        const _targets = Array.isArray(table) ? table : [ table ];
        if (_targets.includes(this.table)){
            this.account?.DoRefresh('TICKET', false);
        }

        super.DoRefresh(table, upwards);
    }

    protected override DoChildLinked(relation: string | null, related: RelatedObject | null): void {
        if (!relation){
            return;
        }

        if (relation === 'account'){
            related?.DoRefresh('TICKET', false);
            return;
        }

        if (relation === 'qrcode'){
            if (!this.CopyOf && related instanceof QrCode){
                if (related === this.qrcode){
                    related.AddTicket(this);
                }
                else {
                    related.DelTicket(this);
                }
            }

            this.DoRefresh('QRCODE', true);
            return;
        }

        if (relation === 'place'){
            if (this.CopyOf || !(related instanceof Place)){
                return;
            }

            if (related === this.place){
                related.AddTicket(this);
            }
            else {
                related.DelTicket(this);
            }
            return;
        }

        if (relation === 'invceac'){
            if (related instanceof AccountInvoice){
                related.AddTicket(this);
            }

            this.account?.DoRefresh('TICKET', false);
        }
    }

    /****************************/
    /* FLAGS MANAGEMENT         */
    /****************************/

    protected get flagArray(): Array<boolean> {
        const _array: boolean[] = new Array(Object.keys(TicketFlags).length / 2).fill(false);
        
        if (this.flags){
            const _flags = Array.from(this.flags);
            for(let i=0; i < _flags.length; i++){
                _array[i] = (_flags[i] == '1');
            }        
        }

        return _array;
    }

    protected set flagArray(value: Array<boolean>) {
        const _array: string[] = [];
        for(let i=0; i < value.length; i++){
            _array[i] = (value[i] === true) ? '1' : '0';
        }

        const _flags = _array.join('');
        if (!this.patchValue(this._data, 'flags', _flags)){
            return;
        }

        this.ToUpdate = true;
    }

    protected _setFlagValue(flag: TicketFlags, value: boolean){
        const _flags = this.flagArray;
        _flags[flag] = value;
        this.flagArray = _flags;        
    }

    get PrintedFlag(): boolean {
        return this.flagArray[TicketFlags.OrderPrinted];
    }

    set PrintedFlag(value: boolean){
        this._setFlagValue(TicketFlags.OrderPrinted, value);
    }

    get IsNotifiedFlag(): boolean {
        return this.flagArray[TicketFlags.ReadyNotified];
    }

    set IsNotifiedFlag(value: boolean){
        this._setFlagValue(TicketFlags.ReadyNotified, value);
    }

    get ReadyFlag() : boolean {
        return this.flagArray[TicketFlags.TicketReady];
    }

    set ReadyFlag(value: boolean){
        this._setFlagValue(TicketFlags.TicketReady, value);
    }

    get ToPayFlag() : boolean {
        return this.flagArray[TicketFlags.TicketToPay];
    }

    set ToPayFlag(value: boolean){
        this._setFlagValue(TicketFlags.TicketToPay, value);
    }

    get PaidFlag() : boolean {
        return this.flagArray[TicketFlags.TicketPaid];
    }

    set PaidFlag(value: boolean){
        this._setFlagValue(TicketFlags.TicketPaid, value);
    }

    get ReopenedFlag() : boolean {
        return this.flagArray[TicketFlags.TicketReopened];
    }

    set ReopenedFlag(value: boolean){
        this._setFlagValue(TicketFlags.TicketReopened, value);
    }

    /**
     * Sticky flag: once set, the ticket must be committed on cancel/close and
     * cannot be discarded in memory (anti-fraud). Clearing lines does not reset it.
     * Set when the first real (non-splitter) product is added.
     */
    get ToCommitFlag(): boolean {
        return this.flagArray[TicketFlags.TicketToCommit];
    }

    set ToCommitFlag(value: boolean){
        if (value !== true){
            return;     // sticky: never clear once set
        }

        this._setFlagValue(TicketFlags.TicketToCommit, true);
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/
    
    get status(): string | null {
        return this['__base__status'];
    }

    set status(value: string){
        if (this.status == 'DE'){
            return;     // cannot modify deleted items
        }

        if (this.status == value){
            return;
        }

        this['__base__status'] = value;

        if (this.status == 'RD'){
            this.ReadyFlag = true;
        }

        if (this.place){
            if ((this.status == 'DE') && !this.Commited){
                this.place.DelTicket(this);
            }
            else {
                this.place.DoRefresh(this.table);
            }
        }

        if (this.qrcode){
            if ((this.status == 'DE') && !this.Commited){
                this.qrcode.DelTicket(this);
            }
            else {
                this.qrcode.DoRefresh(this.table);
            }
        }

        this.ToUpdate = true;
    }   

    get payment(): TicketPayment | null {
        const _payment = this['__base__payment'];

        // backwards compatibility
        switch(_payment){
            case 'FORCEPAY':
            case 'CASHPAY':
                return 'PAYCASH';
            case 'CARDPAY':
                return 'PAYCARD';
            case 'PAYPAL':
                return 'PAYLINE';
        }
        
        return _payment;
    }

    set payment(value: TicketPayment | null) {
        this['__base__payment'] = value;

        if (!value){
            this.PaidFlag = false;
            this.ToPayFlag = false;
        }
        else if (!this.PaidFlag){
            this.PaidFlag = false;
            this.ToPayFlag = true;
        }

        this.ToUpdate = true;
    }

    /**
     * Consolidates commercial close: paid, no pending request, and status
     * `PP` for `PAYACCOUNT` or `PD` for till/online methods.
     * Does not set `paidin` / `paidon` / `till` (view / ACT-CAJA).
     */
    MarkPaid(): void {
        this.PaidFlag = true;
        this.ToPayFlag = false;

        this.status = (this.payment === 'PAYACCOUNT') ? 'PP' : 'PD';
    }

    get account(): PayAccount | null {
        return (this._children['account'] as PayAccount) || null;
    }

    set account(value: PayAccount | null) {
        if (this.SetChild('account', value, 'account')) {
            this.ToUpdate = true;
        }
    }

    get invoice(): TicketInvoice | null {
        for(const _invoice of this.invoices){
            if (_invoice.IsValid){
                return _invoice;
            }
        }

        return null;
    }

    set invoice(value: TicketInvoice | null){
        for (const _invoice of [...this.invoices]) {
            if (_invoice === value) {
                continue;
            }
            
            _invoice.status = 'DE';
        }

        if (value != null){
            this.AddInvoice(value);
        }
    }

    get ticketdata(): TicketData | null {
        if (this.datas.length === 0){
            return null;
        }

        const _sorted = [...this.datas].sort((a, b) => {
            const ta = (a.updated || a.created || new Date(0)).getTime();
            const tb = (b.updated || b.created || new Date(0)).getTime();

            return tb - ta;
        });

        return _sorted[0];
    }

    /****************************/
    /* CUSTOM METHODS           */
    /****************************/

    get IsValid(): boolean {
        return (!!this.status && (this.status != 'DE'));
    }  
    
    get IsCancelled(): boolean {
        if (this.status){
            return ['CC', 'DE'].includes(this.status);
        }
        return false;
    }

    get IsDeleted(): boolean {
        return (!!this.status && (this.status == 'DE'));
    }

    get iserror(): boolean {
        return this._iserror;
    }

    set iserror(value: boolean){
        this._iserror = (value === true);
    }

    /**
     * Ticket is still in the recent window: created or paid within `recentMs`.
     * Last `updated` does not count (kitchen marks must not keep old tickets recent).
     */
    get IsRecent(): boolean {
        const limit = Date.now() - AppConstants.recentMs;
        if (this.created && this.created.getTime() > limit) {
            return true;
        }

        if (this.paidon && this.paidon.getTime() > limit) {
            return true;
        }

        return false;
    }

    get client(): Client | null {
        const _invoice = this.invoice;
        if (_invoice?.client){
            return _invoice.client;
        }

        const _invceac = this.invceac;
        if (_invceac?.account?.client){
            return _invceac.account.client;
        }

        const _account = this.account;
        if (_account?.client){
            return _account.client;
        }

        return null;
    }

    get source(): TicketSource {
        let source = TicketSource.BR;
        if (this.qrcode && this.session){
            source = this.session.user ? TicketSource.SF : TicketSource.QR;
        }

        return source;
    }

    /**
     * Direct tickets skip kitchen tracking: the place does not track tickets,
     * except QR tickets that opt into `optionQrTrackTicket`.
     */
    get IsDirect(): boolean {
        if (!this.place || this.place.TicketPreparation){
            return false;
        }

        if (this.source == TicketSource.QR){
            return !this.place.GetConfigOption('optionQrTrackTicket');
        }

        return true;
    }

    /**
     * Order is ready for the guest: direct service, kitchen status `RD`, or ready flag.
     */
    get IsReady(): boolean {
        return this.IsDirect || (this.status == 'RD') || this.ReadyFlag;
    }

    /**
     * Whether this ticket belongs to the current session, is valid, and is still recent.
     */
    get InSession(): boolean {
        return (this.session === this.data.session) && this.IsValid && this.IsRecent;
    }

    /****************************/
    /* PRIVATE METHODS          */
    /****************************/

    private _IsRecentCheck(): void {
        const recent = this.IsRecent;
        if (!recent) {
            if (this._oneminute_subscription) {
                this._oneminute_subscription.unsubscribe();
                this._oneminute_subscription = null;
            }
            this._isrecent = false;
            return;
        }

        this._isrecent = recent;
        if (!this._oneminute_subscription) {
            this._oneminute_subscription = this.data.clock.OnOneMinuteTick.subscribe(() => {
                this._onOneMinuteTick();
            });
        }
    }

    private _onOneMinuteTick(): void {
        if (!this.IsValid || this.IsDeleted) {
            if (this._oneminute_subscription) {
                this._oneminute_subscription.unsubscribe();
                this._oneminute_subscription = null;
            }
            return;
        }

        const isRecent = this.IsRecent;
        if (!isRecent) {
            if (this._oneminute_subscription) {
                this._oneminute_subscription.unsubscribe();
                this._oneminute_subscription = null;
            }
        }

        if (this._isrecent === isRecent) {
            return;
        }

        this._isrecent = isRecent;
        this.DoRefresh('TICKET', true);
    }
}

